/**
 * Socket Plugins Index
 * Main entry point for socket-related plugins
 */

import { defineNuxtPlugin } from '#app';

export { ConnectionManager, connectionManager, createConnectionManager } from './connection.js';
export { NotificationManager, notificationManager, createNotificationManager } from './notification.js';

// Import factory functions
import { createConnectionManager } from './connection.js';
import { createNotificationManager } from './notification.js';

// Combined socket manager that integrates both connection and notification functionality
export class SocketManager {
  constructor(config = {}) {
    this.connectionManager = createConnectionManager(config.connection);
    this.notificationManager = createNotificationManager(config.notification);
    
    // Set up integration between connection and notifications
    this._setupIntegration();
  }

  /**
   * Setup integration between connection and notification managers
   */
  _setupIntegration() {
    this.connectionManager.setEventHandlers({
      onStatusChange: (status, data) => {
        this.notificationManager.showConnectionStatus(status, data);
      },
      onMessage: (message) => {
        if (message.type !== 'ping' && message.type !== 'pong') {
          this.notificationManager.showMessageNotification(message);
        }
      },
      onError: (error) => {
        this.notificationManager.showNotification({
          type: 'error',
          payload: { error: error.message }
        }, {
          title: '❌ Connection Error',
          body: error.message,
          urgent: true
        });
      }
    });
  }

  /**
   * Connect to WebSocket server
   */
  async connect(url) {
    return await this.connectionManager.connect(url);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.connectionManager.disconnect();
  }

  /**
   * Send message
   */
  sendMessage(data) {
    return this.connectionManager.sendMessage(data);
  }

  /**
   * Send ping
   */
  sendPing() {
    return this.connectionManager.sendPing();
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.connectionManager.getConnectionStatus();
  }

  /**
   * Get statistics
   */
  getStats() {
    return this.connectionManager.getStats();
  }

  /**
   * Update configuration
   */
  updateConfig(config) {
    if (config.connection) {
      this.connectionManager.updateConfig(config.connection);
    }
    if (config.notification) {
      this.notificationManager.updateConfig(config.notification);
    }
  }

  /**
   * Show notification
   */
  showNotification(message, options) {
    return this.notificationManager.showNotification(message, options);
  }

  /**
   * Clear all notifications
   */
  clearNotifications() {
    this.notificationManager.clearAllNotifications();
  }

  /**
   * Clean up all resources
   */
  cleanup() {
    this.connectionManager.cleanup();
    this.notificationManager.cleanup();
  }
}

// Export factory function for integrated socket manager
export function createSocketManager(config) {
  return new SocketManager(config);
}

// Export default instance (only on client-side)
export const socketManager = typeof window !== 'undefined' ? new SocketManager() : null;

// Nuxt plugin default export
export default defineNuxtPlugin(() => {
  // Only initialize on client-side
  if (process.client) {
    return {
      provide: {
        socketManager: socketManager,
        socket: {
          manager: socketManager,
          connect: (url) => socketManager.connect(url),
          disconnect: () => socketManager.disconnect(),
          send: (data) => socketManager.sendMessage(data),
          status: () => socketManager.getConnectionStatus(),
          stats: () => socketManager.getStats(),
          notify: (message, options) => socketManager.showNotification(message, options),
          clearNotifications: () => socketManager.clearNotifications()
        }
      }
    };
  } else {
    // Server-side fallback
    return {
      provide: {
        socketManager: null,
        socket: {
          manager: null,
          connect: () => Promise.resolve(false),
          disconnect: () => {},
          send: () => false,
          status: () => 'disconnected',
          stats: () => ({}),
          notify: () => {},
          clearNotifications: () => {}
        }
      }
    };
  }
});
